/**
 * Haptic feedback module — wraps cordova-plugin-taptic-engine for iOS.
 *
 * In Cordova context: uses the native Taptic Engine for crisp haptic feedback.
 * In browser context: falls back to navigator.vibrate when available.
 * Unsupported browsers (including iOS Safari) silently no-op.
 *
 * Usage:
 *   import { hapticLift, hapticDrop } from './haptics'
 *   hapticLift()   // light tap when picking up a card
 *   hapticDrop()   // light tap when placing a card
 */

import { appState } from './state'

// ─── TapticEngine type (from cordova-plugin-taptic-engine) ──────────────────

interface TapticEngine {
    impact(
        options: { style: 'light' | 'medium' | 'heavy' },
        success?: () => void,
        error?: (err: string) => void
    ): void
    selection(success?: () => void, error?: (err: string) => void): void
    notification(
        options: { type: 'success' | 'warning' | 'error' },
        success?: () => void,
        error?: (err: string) => void
    ): void
}

declare global {
    interface Window {
        TapticEngine?: TapticEngine
    }
}

// ─── Internal helper ────────────────────────────────────────────────────────

function isAutomatedTestMode(): boolean {
    if (typeof window === 'undefined') return false

    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('test') !== null || navigator.webdriver === true
}

function triggerVibrate(pattern: number | number[]): void {
    if (typeof navigator === 'undefined') return
    if (typeof navigator.vibrate !== 'function') return

    try {
        navigator.vibrate(pattern)
    } catch {
        // Best-effort fallback only.
    }
}

function isAndroidBrowser(): boolean {
    if (typeof navigator === 'undefined') return false
    return /Android/i.test(navigator.userAgent)
}

function triggerImpact(style: 'light' | 'medium' | 'heavy'): void {
    if (!appState.settings.hapticsEnabled) return
    if (isAutomatedTestMode()) return

    if (typeof window === 'undefined') return

    const engine = window.TapticEngine
    if (engine) {
        engine.impact({ style })
        return
    }

    // Browser fallback (best effort).
    // Android web typically needs slightly longer pulses to feel comparable.
    const durations = isAndroidBrowser()
        ? { light: 12, medium: 18, heavy: 28 }
        : { light: 8, medium: 12, heavy: 18 }

    if (style === 'light') {
        triggerVibrate(durations.light)
    } else if (style === 'medium') {
        triggerVibrate(durations.medium)
    } else {
        triggerVibrate(durations.heavy)
    }
}

// ─── Public API ─────────────────────────────────────────────────────────────

/** Medium tap — feels like lifting a card off the table. */
export function hapticLift(): void {
    triggerImpact('medium')
}

/** Medium tap — feels like setting a card down on the table. */
export function hapticDrop(): void {
    triggerImpact('medium')
}
